import { useState } from 'react';
import { TextInput, Textarea } from '@mantine/core';
import { IconTrash, IconPencil, IconCheck, IconX } from '@tabler/icons-react';
import { FlexibleDateInput } from '../medicalHistory/FlexibleDateInput';
import type { MedicationLocal } from '../../types/medications';
import type { FlexibleDate } from '../../types/medicalHistory';

interface MedicationFormProps {
  medication: MedicationLocal;
  onChange: (updated: MedicationLocal) => void;
  onRemove: () => void;
  onSubmit: () => void;
  disabled?: boolean;
  isNew?: boolean;
}

// Format date for display
function formatDate(date: FlexibleDate | null | undefined): string {
  if (!date) return '';
  const { value, precision } = date;
  if (precision === 'year') return value;
  if (precision === 'month') {
    const [year, month] = value.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month, 10) - 1]} ${year}`;
  }
  const d = new Date(value);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function MedicationForm({
  medication,
  onChange,
  onRemove,
  onSubmit,
  disabled = false,
  isNew = false,
}: MedicationFormProps) {
  const [isEditing, setIsEditing] = useState(isNew);

  const handleChange = (
    field: keyof MedicationLocal,
    value: string | FlexibleDate | null | undefined
  ) => {
    onChange({ ...medication, [field]: value });
  };

  const handleSave = () => {
    if (medication.medicationName.trim()) {
      onSubmit();
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    if (isNew) {
      onRemove();
    } else {
      setIsEditing(false);
    }
  };

  // View mode - display as card
  if (!isEditing) {
    return (
      <div className="card p-4 group">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h4 className="font-medium text-gray-200">{medication.medicationName}</h4>
            <div className="mt-1 text-sm text-gray-400 space-y-0.5">
              {(medication.dosage || medication.frequency) && (
                <p>
                  {[medication.dosage, medication.frequency].filter(Boolean).join(' — ')}
                </p>
              )}
              {medication.startDate && (
                <p>Since: {formatDate(medication.startDate)}</p>
              )}
              {medication.comments && (
                <p className="text-gray-500 italic">{medication.comments}</p>
              )}
            </div>
          </div>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              disabled={disabled}
              className="p-1.5 rounded-lg text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors disabled:opacity-50"
              title="Edit"
            >
              <IconPencil size={16} />
            </button>
            <button
              type="button"
              onClick={onRemove}
              disabled={disabled}
              className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
              title="Delete"
            >
              <IconTrash size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Edit mode - show form
  return (
    <div className="card p-4 border-cyan-500/30">
      <div className="space-y-4">
        {/* Medication Name */}
        <TextInput
          label="Medication Name"
          placeholder="e.g., Lisinopril, Metformin, Ibuprofen"
          value={medication.medicationName}
          onChange={(e) => handleChange('medicationName', e.target.value)}
          disabled={disabled}
          required
          classNames={{
            input: 'input',
            label: 'text-sm font-medium text-gray-300 mb-1',
          }}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Dosage */}
          <TextInput
            label="Dosage"
            placeholder="e.g., 10mg, 500mg, 2 tablets"
            value={medication.dosage || ''}
            onChange={(e) => handleChange('dosage', e.target.value || undefined)}
            disabled={disabled}
            classNames={{
              input: 'input',
              label: 'text-sm font-medium text-gray-300 mb-1',
            }}
          />

          {/* Frequency */}
          <TextInput
            label="Frequency"
            placeholder="e.g., Once daily, Twice daily with meals"
            value={medication.frequency || ''}
            onChange={(e) => handleChange('frequency', e.target.value || undefined)}
            disabled={disabled}
            classNames={{
              input: 'input',
              label: 'text-sm font-medium text-gray-300 mb-1',
            }}
          />
        </div>

        {/* Start Date */}
        <FlexibleDateInput
          label="When did you start taking this?"
          value={medication.startDate ?? null}
          onChange={(date) => handleChange('startDate', date)}
          disabled={disabled}
        />

        {/* Comments */}
        <Textarea
          label="Comments"
          placeholder="Share anything helpful — e.g., what it's prescribed for, how well it works, any side effects"
          value={medication.comments || ''}
          onChange={(e) => handleChange('comments', e.target.value || undefined)}
          disabled={disabled}
          minRows={2}
          classNames={{
            input: 'input',
            label: 'text-sm font-medium text-gray-300 mb-1',
          }}
        />

        {/* Action buttons */}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={handleCancel}
            disabled={disabled}
            className="px-3 py-1.5 text-sm text-gray-400 hover:text-gray-200 transition-colors disabled:opacity-50 flex items-center gap-1"
          >
            <IconX size={14} />
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={disabled || !medication.medicationName.trim()}
            className="px-3 py-1.5 text-sm bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <IconCheck size={14} />
            {isNew ? 'Add Medication' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
